import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Zap, Sparkles, Heart, Check } from "lucide-react";
import { useSuperPop } from "@/hooks/useSuperPop";
import { toast } from "@/hooks/use-toast";

interface SuperPopModalProps {
  isOpen: boolean;
  onClose: () => void;
  targetProfile: {
    id: string;
    name: string;
    photo_url?: string;
  } | null;
  onSuccess: () => void;
}

const SuperPopModal = ({ isOpen, onClose, targetProfile, onSuccess }: SuperPopModalProps) => {
  const { superPop, canUseSuperPop, loading, hasSuperPopped } = useSuperPop();
  const [showSuccess, setShowSuccess] = useState(false);

  if (!targetProfile) return null;

  const alreadySuperPopped = hasSuperPopped(targetProfile.id);

  const handleSuperPop = async () => {
    const result = await superPop(targetProfile.id);

    if (result.success) {
      setShowSuccess(true);
      toast({
        title: "⚡ Super Pop Sent!",
        description: `${targetProfile.name} will see you first!`,
      });
      setTimeout(() => {
        setShowSuccess(false);
        onSuccess();
        onClose();
      }, 1500);
    } else {
      toast({
        title: "Super Pop Failed",
        description: result.error,
        variant: "destructive",
      });
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-center gap-2 text-center">
            <Zap className="w-6 h-6 text-amber-500" />
            Super Pop
          </DialogTitle>
        </DialogHeader>

        <div className="text-center py-4">
          {/* Profile preview */}
          <div className="relative w-24 h-24 rounded-full mx-auto mb-4 overflow-hidden bg-muted ring-4 ring-amber-500/30">
            {targetProfile.photo_url ? (
              <img
                src={targetProfile.photo_url}
                alt={targetProfile.name}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <span className="text-3xl font-bold">{targetProfile.name[0]}</span>
              </div>
            )}
            {showSuccess && (
              <div className="absolute inset-0 bg-amber-500 flex items-center justify-center">
                <Check className="w-10 h-10 text-white" />
              </div>
            )}
          </div>

          <h3 className="text-lg font-semibold mb-1">{targetProfile.name}</h3>

          {alreadySuperPopped ? (
            <p className="text-sm text-muted-foreground">
              You've already Super Popped {targetProfile.name}!
            </p>
          ) : (
            <>
              <p className="text-sm text-muted-foreground mb-4">
                Stand out from the crowd! Your Super Pop will:
              </p>

              <div className="space-y-2 text-left bg-amber-500/10 rounded-xl p-4 mb-4">
                <div className="flex items-center gap-2 text-sm">
                  <Zap className="w-4 h-4 text-amber-500" />
                  <span>Send an immediate notification</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <Heart className="w-4 h-4 text-amber-500" />
                  <span>Show you at the top of their queue</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <Sparkles className="w-4 h-4 text-amber-500" />
                  <span>3x more likely to match!</span>
                </div>
              </div>
            </>
          )}
        </div>

        {alreadySuperPopped ? (
          <Button onClick={onClose} variant="outline" className="w-full">
            Close
          </Button>
        ) : (
          <Button
            onClick={handleSuperPop}
            disabled={loading || !canUseSuperPop}
            className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600"
          >
            <Zap className="w-4 h-4 mr-2" />
            {loading ? "Sending..." : "Super Pop!"}
          </Button>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default SuperPopModal;
